/* eslint-disable @typescript-eslint/no-explicit-any */
import type {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
  AxiosRequestHeaders,
} from "axios";

import axios from "axios";
import pkg from "../../../package.json";

const APP_VERSION = pkg.version;
let tokenCache: string | null = null;
let tokenCacheLoaded = false;

async function loadTokenCacheOnce() {
  if (tokenCacheLoaded) return;
  tokenCacheLoaded = true;

  try {
    if (typeof localStorage !== "undefined") {
      tokenCache = localStorage.getItem("token");
    }
  } catch {
    tokenCache = null;
  }
}

function setToken(token: string) {
  tokenCache = token;
}

export function getToken() {
  return tokenCache;
}

export function transformUrl(url: string): string {
  const env = process.env;
  if (env.VUE_APP_ENV === "custom") return url;
  if (env.VUE_APP_ENV === "development") {
    url = url.replace("/api", "");
  }
  if (url.indexOf("http") === 0) return url;
  url = `${env.VUE_APP_AXIOS_URL ? env.VUE_APP_AXIOS_URL : ""}${url}`;
  return url;
}

export function mergeHeaders(
  headers?: AxiosRequestHeaders
): AxiosRequestHeaders {
  headers = headers || {};
  !headers["Content-Type"] && (headers["Content-Type"] = "application/json");
  headers["User-Agent"] = `DianshuClient/${APP_VERSION}`;
  headers["Client-Version"] = APP_VERSION; // 添加客户端版本号到所有请求
  const token = getToken();
  token && (headers["token"] = token);
  return headers as AxiosRequestHeaders;
}

export interface Result<T = any> {
  page: any;
  resultCode: number;
  resultDesc: string;
  data: T;
  dataList: T;
}

const serve: AxiosInstance = axios.create({
  withCredentials: true,
  timeout: 1000 * 60 * 60 * 1.5,
});

// 请求前拦截
serve.interceptors.request.use(async (config) => {
  await loadTokenCacheOnce();
  if (process.env.VUE_APP_ENV !== "custom") {
    config.url = transformUrl(config.url as string);
  }
  config.headers = mergeHeaders(config.headers);
  return config;
});

// 请求响应拦截
serve.interceptors.response.use(
  (res: AxiosResponse<Result>) => {
    return new Promise((resolve, reject) => {
      const token = res.headers["token"];
      token && setToken(token);
      const { resultCode } = res.data;
      if (resultCode === 100) {
        return resolve(res);
      }
      // 将非 100 的返回包装为 Error，保留上下文，便于 Sentry 捕获堆栈
      const err = new Error(res.data?.resultDesc || "Request failed");
      (err as any).resultCode = res.data?.resultCode;
      (err as any).resultDesc = res.data?.resultDesc;
      (err as any).data = res.data?.data;
      return reject(err);
    });
  },
  (error) => {
    const { code, message } = error || {};
    const err: string = (error && error.toString && error.toString()) || "";
    let resultDesc = "";
    try {
      if (code === "ECONNABORTED" && message.indexOf("timeout") !== -1) {
        resultDesc = "接口请求超时，请刷新页面重试";
      }
      if (err.includes("Network Error")) {
        resultDesc = "网络异常请检查你的网络链接是否正常";
      }
    } catch (e) {
      throw new Error(e as unknown as string);
    }
    error.resultDesc = resultDesc;
    return Promise.reject(error);
  }
);

const request = <T = any>(config: AxiosRequestConfig): Promise<T> => {
  return new Promise((resolve, reject) => {
    serve
      .request<any, AxiosResponse<Result>>(config)
      .then((res: AxiosResponse<Result>) => {
        if (res.data.data) {
          if (res.data.page) {
            resolve({
              data: res.data.data,
              page: res.data.page,
            } as unknown as Promise<T>);
          }
          resolve(res.data.data as Promise<T>);
        } else if (res.data.dataList && res.data.dataList.length) {
          resolve(res.data.dataList as Promise<T>);
        } else {
          resolve(res.data.data as Promise<T>);
        }
      })
      .catch((e: Error | AxiosError) => {
        reject(e);
      });
  });
};

export default request;
